import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { IconCalendarOutline } from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    getNextPayoutDate,
    putBasicPayoutSettings
} from 'services/endpoints/settings/general/payout'
import {
    useSetUserProfile,
    useUserProfile
} from 'store'

import styles from './_PaymentFrequency.module.scss'

export const PaymentFrequency = () => {
    const { t } = useTranslation()
    const profile = useUserProfile()!
    const queryClient = useQueryClient()

    const [currentFrequency, setCurrentFrequency] = useState(
        profile.payout_frequency
    )

const { mutate } = useMutation(
    async (vars: { freq: string; method: string | null }) =>
        await putBasicPayoutSettings({
            payout_frequency: vars.freq,
            payout_method: vars.method
        }),
        {
            onMutate: vars => {
                const previousFrequency = currentFrequency

                // Optimistic local UI update
                setCurrentFrequency(vars.freq)

                return {
                    previousFrequency
                }
            },

            onSuccess: (response, vars) => {
                /**
                 * Assuming response shape:
                 *
                 * {
                 *   status: 'OK',
                 *   code: 200,
                 *   data: {
                 *      payout_frequency: 'manual',
                 *      ...
                 *   }
                 * }
                 */

                const updatedFrequency =
                    response?.data?.payout_frequency ?? vars.freq

                setCurrentFrequency(updatedFrequency)

                // Update Zustand immediately
                useSetUserProfile({
                    payout_frequency: updatedFrequency
                })

                // Optional:
                // If payout_method can also be modified/normalized by backend,
                // keep that cached too.
                if (response?.data?.payout_method) {
                    useSetUserProfile({
                        payout_method: response.data.payout_method
                    })
                }

                // addToast('success', t('successSettingPayout'))
            },

            onError: (_error, _vars, context) => {
                // Roll back optimistic UI change
                if (context?.previousFrequency) {
                    setCurrentFrequency(context.previousFrequency)
                }

                addToast(
                    'error',
                    t('Set Bank information and payout method first')
                )
            },

            onSettled: () => {
                /**
                 * Still refresh the server-backed React Query profile,
                 * but Zustand no longer has to wait for this request.
                 */
                queryClient.invalidateQueries('user-profile')
            }
        }
    )

    const getMinimumPayoutText = () => {
        const country = profile.country
        const method = profile.payout_method

        if (!method) {
            return 'the minimum'
        }

        if (country === 'United States' && method === 'wire') {
            return '$20'
        }

        return '$50'
    }

    const minPayoutText = getMinimumPayoutText()

    const shouldFetchNextPayoutDate =
        currentFrequency === 'weekly' ||
        currentFrequency === 'monthly'

    const { data: nextPayoutData } = useQuery(
        ['next-payout-date', currentFrequency],
        async () =>
            await getNextPayoutDate(
                currentFrequency as 'weekly' | 'monthly'
            ),
        {
            enabled: shouldFetchNextPayoutDate,
            keepPreviousData: true
        }
    )

    const nextPayoutDate =
        nextPayoutData?.next_payout_date_formatted || ''

    return (
        <WithHeaderSection
            customClass={styles.whs}
            withoutBorder={false}
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('settings:payoutSettings')}
                    title={t('payoutFrequency')}
                />
            }
        >
            {/* Manual */}
            <ActionCard
                icon={<IconCalendarOutline />}
                iconSize='small'
                text={t('manual')}
                hasRadio
                toggleActive={currentFrequency === 'manual'}
                clickFn={() => {
                    mutate({
                        freq: 'manual',
                        method: profile.payout_method
                    })
                }}
            />

            {/* Weekly */}
            <ActionCard
                icon={<IconCalendarOutline />}
                iconSize='small'
                text={t('weekly')}
                hasRadio
                toggleActive={currentFrequency === 'weekly'}
                clickFn={() => {
                    mutate({
                        freq: 'weekly',
                        method: profile.payout_method
                    })
                }}
            />

            {currentFrequency === 'weekly' && (
                <p className={styles.nextPayout}>
                    {t('Once your balance is over')}{' '}
                    <span className={styles.minPayout}>
                        {minPayoutText}
                    </span>{' '}
                    {t('next payout will be scheduled on')}{' '}
                    <span>{nextPayoutDate}</span>
                </p>
            )}

            {/* Monthly */}
            <ActionCard
                icon={<IconCalendarOutline />}
                iconSize='small'
                text={t('monthly')}
                hasRadio
                toggleActive={currentFrequency === 'monthly'}
                clickFn={() => {
                    mutate({
                        freq: 'monthly',
                        method: profile.payout_method
                    })
                }}
            />

            {currentFrequency === 'monthly' && (
                <p className={styles.nextPayout}>
                    {t('Once your balance is over')}{' '}
                    <span className={styles.minPayout}>
                        {minPayoutText}
                    </span>{' '}
                    {t('next payout will be scheduled on')}{' '}
                    <span>{nextPayoutDate}</span>
                </p>
            )}
        </WithHeaderSection>
    )
}